import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Merchant, MerchantFieldSetValueMap } from "client/lib/apt-merchant";
import deepmerge from "client/lib/deepmerge";
import { RestoreResponse, StoreLocationsFile } from "client/types";
import {
  createMerchantFromJson,
  transformAccountValues,
  transformResponse,
} from "client/utils/transforms";

import { merchantAccountInitialState } from "./constants";

const initialState = {
  fieldValueMap: Merchant.getInstance().toFieldValueMap(),
  merchantAccount: merchantAccountInitialState,
  storeLocationsFile: {
    name: "",
    path: "",
  },
};

function isFieldValueMap(
  response: MerchantFieldSetValueMap | RestoreResponse
): response is MerchantFieldSetValueMap {
  return response?.hasOwnProperty("lead");
}

export const merchantSlice = createSlice({
  name: "merchant",
  initialState: initialState,
  reducers: {
    setMerchantAccount: (state, action: PayloadAction<RestoreResponse>) => {
      state.merchantAccount = transformAccountValues(action.payload);
    },
    setMerchantData: (
      state,
      action: PayloadAction<MerchantFieldSetValueMap | RestoreResponse>
    ) => {
      if (isFieldValueMap(action.payload)) {
        // updating the merchant from changes in the UI
        state.fieldValueMap = action.payload;
      } else {
        // updating the merchant from the API response /restore

        // first get what we have in local merchant state
        const localState = Merchant.getInstance().toFieldValueMap();

        // we transform the api response to the merchant field value map first
        // we're checking both account/restore to hydrate the values, for edge cases when restore === null
        const rawState = transformResponse(action.payload);
        const merchant = createMerchantFromJson(rawState);
        const restoredState = merchant.toFieldValueMap();
        // then we get a merchant field value map from current merchant and merge it with response to fill in any gaps. Giving priority to any keys coming from API response
        const restorePriority = deepmerge(localState, restoredState);
        state.fieldValueMap = restorePriority as MerchantFieldSetValueMap;
      }
    },
    /**
     * This probably belongs in a field. I created the field. Until we have a base collection class
     *  though, there is nowhere to put it. merchant.collections is an array of FieldSets. And I
     *  don't think this belongs on merchant, and it certainly doesn't belong on .locations[0].
     *
     * I think this belongs on merchant.storeLocationCollection, which will be a class that has
     *  the fieldSet array on it, and also can have properties like this, and also handle the
     *  add / remove logic that's cluttering up merchant.ts
     *
     * The above is out of scope for this PR though.
     */
    setStoreLocationUpload: (
      state,
      action: PayloadAction<StoreLocationsFile>
    ) => {
      state.storeLocationsFile = action.payload;
    },
    setOnboardingLink: (state, action: PayloadAction<string>) => {
      state.merchantAccount.onboardingLink = action.payload;
    },
  },
});

export const merchantActions = merchantSlice.actions;
